import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  getUsers,
  getAdminStats,
  getVideosReport,
  getAdminVideosPage,
  updateAdminVideoComment,
  getAdminVideoJobs,
  getAdminJobCurl,
  retryAdminJobUpload,
  rerenderAdminJob,
  getIntegrationsStatus,
  refreshIntegrationsStatus,
  getStatusRecords,
  addUserRole,
} from "@app/services/admin";
import { downloadVideosReportExcel } from "@app/services/adminReport";
import { videoKey } from "@app/hooks/videos";

export const adminKeys = {
  all: ["admin"],
  users: () => [...adminKeys.all, "users"],
  stats: (filters) => [...adminKeys.all, "stats", filters],
  videos: (filters = {}) => [...adminKeys.all, "videos", filters],
  videoJobs: (videoId) => [...adminKeys.all, "videoJobs", videoId],
  integrations: (complex = false) => [
    ...adminKeys.all,
    "integrations",
    { complex },
  ],
  statusRecords: () => [...adminKeys.all, "integrations", "records"],
};

export const useAdminUsers = (options = {}) => {
  return useQuery({
    queryKey: adminKeys.users(),
    queryFn: ({ signal }) => getUsers({ signal }),
    ...options,
  });
};

export const useAdminStats = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: adminKeys.stats(filters),
    queryFn: ({ signal }) => getAdminStats(filters, { signal }),
    ...options,
  });
};

export const ADMIN_VIDEOS_PAGE_SIZE = 50;

export const useAdminVideos = (filters = {}, options = {}) => {
  return useInfiniteQuery({
    queryKey: adminKeys.videos({
      ...filters,
      pageSize: ADMIN_VIDEOS_PAGE_SIZE,
    }),
    queryFn: ({ pageParam, signal }) =>
      getAdminVideosPage(
        { ...filters, page: pageParam, pageSize: ADMIN_VIDEOS_PAGE_SIZE },
        { signal },
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage?.pagination?.page < lastPage?.pagination?.totalPages
        ? lastPage.pagination.page + 1
        : undefined,
    meta: {
      errorMessage: "Failed to load videos. Please try again.",
    },
    ...options,
  });
};

// Applies `update` to admin video rows in both flat and paged cache entries.
const updateAdminVideoRows = (data, update) => {
  if (Array.isArray(data)) return data.map(update);
  if (data?.pages) {
    return {
      ...data,
      pages: data.pages.map((page) => ({
        ...page,
        videos: Array.isArray(page?.videos)
          ? page.videos.map(update)
          : page?.videos,
      })),
    };
  }
  return data;
};

export const useUpdateAdminVideoComment = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, comment }, { signal }) =>
      updateAdminVideoComment(id, comment, { signal }),
    onSuccess: (updatedVideo, variables) => {
      const targetId = updatedVideo?.id ?? variables?.id;
      const nextComment = updatedVideo?.comment ?? variables?.comment ?? "";

      queryClient.setQueriesData(
        { queryKey: [...adminKeys.all, "videos"] },
        (data) =>
          updateAdminVideoRows(data, (video) =>
            video.id === targetId ? { ...video, comment: nextComment } : video,
          ),
      );

      if (targetId) {
        queryClient.invalidateQueries({ queryKey: videoKey.detail(targetId) });
      }
    },
    meta: {
      errorMessage: "Failed to save comment. Please try again.",
    },
    ...options,
  });
};

export const useIntegrationsStatus = (complex = false, options = {}) => {
  return useQuery({
    queryKey: adminKeys.integrations(complex),
    queryFn: ({ signal }) => getIntegrationsStatus({ complex, signal }),
    staleTime: 60_000,
    refetchInterval: 60_000,
    ...options,
  });
};

export const useStatusRecords = (options = {}) => {
  return useQuery({
    queryKey: adminKeys.statusRecords(),
    queryFn: ({ signal }) => getStatusRecords({ signal }),
    staleTime: 60_000,
    refetchInterval: 60_000,
    meta: {
      errorMessage: "Failed to load status records. Please try again.",
    },
    ...options,
  });
};

export const useRefreshIntegrationsStatus = (complex = false, options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ signal } = {}) =>
      refreshIntegrationsStatus({ complex, signal }),
    onSuccess: (data) => {
      queryClient.setQueryData(adminKeys.integrations(complex), data);
      queryClient.invalidateQueries({ queryKey: adminKeys.statusRecords() });
    },
    meta: {
      errorMessage: "Failed to refresh integration status. Please try again.",
    },
    ...options,
  });
};

export const useDownloadVideosReport = (options = {}) => {
  return useMutation({
    mutationFn: async (filters = {}) => {
      const reportName =
        filters.fromDate && filters.toDate
          ? `videos-${filters.fromDate}-${filters.toDate}.xlsx`
          : "videos-all.xlsx";
      const videos = await getVideosReport(filters);
      await downloadVideosReportExcel(videos, reportName);
      return videos.length;
    },
    meta: {
      errorMessage: "Failed to download report. Please try again.",
    },
    ...options,
  });
};

export const useAddUserRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userID, role }) => addUserRole(userID, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminKeys.users() });
    },
  });
};

export const useAdminVideoJobs = (videoId, options = {}) => {
  return useQuery({
    queryKey: adminKeys.videoJobs(videoId),
    queryFn: ({ signal }) => getAdminVideoJobs(videoId, { signal }),
    enabled: Boolean(videoId),
    ...options,
  });
};

export const useAdminJobCurl = (options = {}) => {
  return useMutation({
    mutationFn: ({ jobId }, { signal }) => getAdminJobCurl(jobId, { signal }),
    ...options,
  });
};

export const useAdminJobRetry = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ jobId, action }, { signal }) =>
      action === "rerender"
        ? rerenderAdminJob(jobId, { signal })
        : retryAdminJobUpload(jobId, { signal }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [...adminKeys.all, "videos"] });
    },
    ...options,
  });
};
